import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { COMPANY } from "@/data/content";

const ABOUT_IMG =
  "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const values = [
  {
    t: "Depth over breadth",
    d: "We pick sectors and stay there. Every recruiter on our team has spent years hiring for a single industry.",
  },
  {
    t: "Numbers, not narratives",
    d: "We report time-to-fill, offer-drop rates, and payroll accuracy weekly. If the number is bad, we say so.",
  },
  {
    t: "Long partnerships",
    d: "Our top 20 clients have worked with us for five years or more. It's the metric we care about most.",
  },
  {
    t: "Compliance, always",
    d: "Every mandate — payroll, contract, or permanent — ships with the paperwork already in order.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline text-terracotta mb-4">About Novacore</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.98] text-ink">
              A workforce firm built
              <br />
              by <span className="italic font-light text-forest">HR operators.</span>
            </h1>
            <p className="mt-8 text-lg text-muted2 leading-relaxed max-w-xl">
              Novacore Services Pvt Ltd was founded in {COMPANY.founded} by talent
              acquisition and HR leaders who had spent years running these functions
              inside high-growth companies. We built the firm we wished we could hire.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={ABOUT_IMG} alt="Novacore team meeting" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F1EAEC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <SectionHeading overline="Our approach" title="Four principles that shape every mandate." />
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((v, i) => (
              <div key={v.t} data-testid={`value-${i}`} className="border-t border-forest/20 pt-6">
                <div className="font-display text-2xl font-medium text-ink">{v.t}</div>
                <div className="mt-3 text-muted2 leading-relaxed">{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading overline="By the numbers" title="A few years in, still counting." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { k: COMPANY.hires, v: "Hires placed" },
              { k: COMPANY.clients, v: "Active clients" },
              { k: "12 days", v: "Median close time" },
            ].map((s) => (
              <div key={s.v} data-testid={`about-stat-${s.v.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="font-display text-5xl md:text-6xl font-medium text-forest">{s.k}</div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-muted2">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-forest">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-medium text-bone tracking-tight">
            Want to see if we&rsquo;re the right partner?
          </h2>
          <div className="mt-10 flex justify-center">
            <Link to="/contact" data-testid="about-cta" className="btn-accent">
              Talk to a partner <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
